/**
 * V2StepperSection — V2-native editor section for step.content.stepper
 *
 * UI is based on the retired OnboardingProgressBarSection, with V2 data paths.
 * Reuses V2-owned shared components: ProgressBarItemEditor, PlayPauseButton,
 * AddSlideButton, DeleteItemDialog, DurationRangeSlider.
 *
 * V2 paths: content.stepper.*
 * Replaces: OnboardingProgressBarSection (for V2 context)
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Collapse,
  Divider,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { SAccordion, SortableList } from '@modules/ui-components';
import { OnboardingMediaUploadService } from '@modules/firebase-core/services/OnboardingMediaUploadService';
import { SECTION_MARGIN_BOTTOM, FIELD_MARGIN_BOTTOM } from '../../../config/ui';
import { expandedToggleButtonSx, expandedToggleButtonActiveSx } from '../../../config/editorFieldStyles';
import {
  V2_STEPPER_KIND_OPTIONS,
  DEFAULT_STEPPER_TIMER_DURATION,
  createDefaultV2Segment,
  segmentsToDurations,
  durationsToSegmentRanges,
  getStepperVisibility,
} from '../../../config/v2StepperConfig';
import { createV2StepperStyleSections } from '../../../config/v2StepperStyleConfig';
import { PlayPauseButton, AddSlideButton } from './shared/SliderControls';
import { ProgressBarItemEditor } from './shared/ProgressBarItemEditor';
import { DeleteItemDialog } from './shared/DeleteItemDialog';
import { V2ListStyleSections } from './list/V2ListStyleSections';
import { TiptapTemplateEditor, type LabelBlockType } from '../../shared/templateFields/TiptapTemplateEditor';
import { buildFlowVariableItems } from '../../../config/flowVariablesConfig';
import { variableResolver } from '@modules/v2-variable-utils';
import {
  redistributeAfterAdd,
  redistributeAfterRemove,
  distributeDurationsEvenly,
  calculateRanges,
  handleRangeChange,
  canAddSlide,
  canRemoveSlide,
} from '../../../utils/durationDistribution';
import { getSectionTestId } from '../../../utils/testIdUtils';
import { getValueByPath, getV2TranslationValue } from '../../../utils/v2FieldOperations';
import {
  cloneTemplateItem,
  resetAssetTranslations,
  resetLocalizedLabelContent,
} from '@modules/reactFlow/utils/v2ItemInheritance';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { FlowDocument, InlineCountdownConfig } from '@segmently/schema-models/v2';
import type { FunnelVariable } from '@modules/v2-variable-utils';
import type { ProgressBarTextFieldRenderProps } from '../../../types/progressBarSection.types';

interface SegmentItem {
  progressStart: number;
  progressEnd: number;
  content: Record<string, unknown>;
}

interface EditableSegment {
  id: string;
  originalIndex: number;
  title: string;
  subtitle: string;
  description: string;
  imageUrl: string;
  progressStart: number;
  progressEnd: number;
  durationPercent: number;
  hasConnections: boolean;
}

type StepperTemplateField = 'title' | 'subtitle' | 'description';

export interface V2StepperSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  projectId?: string;
  onboardingId?: string;
  versionId?: string;
  onPreviewPlay?: () => void;
  onPreviewStop?: () => void;
  isPreviewPlaying?: boolean;
  /** Full FlowDocument for BFS-based variable resolution in item text @ menu */
  flowDocument?: FlowDocument | null;
}

export const V2StepperSection: React.FC<V2StepperSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  projectId,
  onboardingId,
  versionId,
  onPreviewPlay,
  onPreviewStop,
  isPreviewPlaying = false,
  flowDocument,
}) => {
  const uploadService = useMemo(() => new OnboardingMediaUploadService(), []);
  const lastUploadedRef = useRef<{ original: string; small: string } | null>(null);

  // State for styles section expansion (gear icon toggle)
  const [stylesExpanded, setStylesExpanded] = useState(false);

  // State for delete confirmation dialog
  const [deleteDialogState, setDeleteDialogState] = useState<{
    open: boolean;
    itemIndex: number;
    itemTitle: string;
    hasConnections: boolean;
  }>({ open: false, itemIndex: -1, itemTitle: '', hasConnections: false });

  const stepperStyleSections = useMemo(() => createV2StepperStyleSections(), []);

  const flowVariableItems = useMemo(() => {
    if (!flowDocument || !step.id) return [];

    const boundVars = variableResolver.findAvailableVariables(flowDocument, step.id);
    const varsRecord: Record<string, FunnelVariable> = {};
    for (const boundVar of boundVars) {
      varsRecord[boundVar.variable.id] = boundVar.variable;
    }

    return buildFlowVariableItems(varsRecord);
  }, [flowDocument, step.id]);

  // Read stepper config
  const kind = useMemo(
    () => getValueByPath<string>(step, 'content.stepper.kind') ?? 'line',
    [step]
  );

  const timerDuration = useMemo(
    () => getValueByPath<number>(step, 'content.stepper.timer.duration') ?? DEFAULT_STEPPER_TIMER_DURATION,
    [step]
  );

  const rawItems = useMemo(() => {
    const items = getValueByPath<SegmentItem[]>(step, 'content.stepper.items');
    return Array.isArray(items) ? items : [];
  }, [step]);

  // Convert to editable segments
  const editableSegments: EditableSegment[] = useMemo(() => {
    const durations = rawItems.length > 0
      ? segmentsToDurations(rawItems)
      : distributeDurationsEvenly(rawItems.length);

    const sum = durations.reduce((s, d) => s + d, 0);
    const normalizedDurations = Math.abs(sum - 100) < 1 ? durations : distributeDurationsEvenly(rawItems.length);

    return rawItems.map((item, index) => ({
      id: `segment-${index}`,
      originalIndex: index,
      title: getV2TranslationValue(step, `content.stepper.items.${index}.content.title`, locale),
      subtitle: getV2TranslationValue(step, `content.stepper.items.${index}.content.subtitle`, locale),
      description: getV2TranslationValue(step, `content.stepper.items.${index}.content.description`, locale),
      imageUrl: (() => {
        const asset = getValueByPath<Record<string, string>>(
          step, `content.stepper.items.${index}.content.image.translations.${locale}`
        );
        if (!asset) return '';
        if (typeof asset === 'string') return asset;
        return asset.original ?? asset.origin ?? '';
      })(),
      progressStart: item.progressStart ?? 0,
      progressEnd: item.progressEnd ?? 0,
      durationPercent: normalizedDurations[index] ?? 0,
      hasConnections: false,
    }));
  }, [step, rawItems, locale]);

  // Get visibility config for current kind
  const visibility = useMemo(() => getStepperVisibility(kind), [kind]);

  // Calculate ranges for two-thumb sliders
  const ranges = useMemo(() => {
    const durations = editableSegments.map((item) => item.durationPercent);
    return calculateRanges(durations);
  }, [editableSegments]);

  // ============================================
  // Kind handlers
  // ============================================

  const handleKindChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      onChange('content.stepper.kind', event.target.value);
    },
    [onChange]
  );

  const handleStylesClick = useCallback(() => {
    setStylesExpanded((prev) => !prev);
  }, []);

  // ============================================
  // Timer handlers
  // ============================================

  const handleTimerDurationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(event.target.value, 10);
      if (!isNaN(value) && value >= 1 && value <= 120) {
        onChange('content.stepper.timer.duration', value);
      }
    },
    [onChange]
  );

  const handlePlay = useCallback(() => {
    if (onPreviewPlay) onPreviewPlay();
  }, [onPreviewPlay]);

  const handleStop = useCallback(() => {
    if (onPreviewStop) onPreviewStop();
  }, [onPreviewStop]);

  // ============================================
  // Range slider handlers
  // ============================================

  const handleItemRangeChange = useCallback(
    (index: number, newRange: [number, number]) => {
      const currentDurations = editableSegments.map((item) => item.durationPercent);
      const newDurations = handleRangeChange(currentDurations, index, newRange);
      const newRanges = durationsToSegmentRanges(newDurations);

      const newItems = rawItems.map((item, idx) => ({
        ...item,
        progressStart: newRanges[idx].progressStart,
        progressEnd: newRanges[idx].progressEnd,
      }));

      onChange('content.stepper.items', newItems);
    },
    [editableSegments, rawItems, onChange]
  );

  // ============================================
  // Item content handlers
  // ============================================

  const handleItemTitleChange = useCallback(
    (index: number, value: string) => {
      onChange(`content.stepper.items.${index}.content.title.translations.${locale}`, value);
    },
    [onChange, locale]
  );

  const handleItemSubtitleChange = useCallback(
    (index: number, value: string) => {
      onChange(`content.stepper.items.${index}.content.subtitle.translations.${locale}`, value);
    },
    [onChange, locale]
  );

  const handleItemDescriptionChange = useCallback(
    (index: number, value: string) => {
      onChange(`content.stepper.items.${index}.content.description.translations.${locale}`, value);
    },
    [onChange, locale]
  );

  const ensureItemTextDynamicKind = useCallback(
    (basePath: string) => {
      const currentKind = getValueByPath<string>(step, `${basePath}.kind`) || 'static';
      if (currentKind !== 'dynamic') {
        onChange(`${basePath}.kind`, 'dynamic');
      }
    },
    [step, onChange]
  );

  const renderTemplateTextField = useCallback(
    (index: number, fieldName: StepperTemplateField, props: ProgressBarTextFieldRenderProps) => {
      const basePath = `content.stepper.items.${index}.content.${fieldName}`;
      const labels = getValueByPath<Record<string, LabelBlockType>>(
        step,
        `${basePath}.templateParams.labels`
      ) || {};
      const links = getValueByPath<Record<string, string>>(
        step,
        `${basePath}.templateParams.links`
      ) || {};
      const countdowns = getValueByPath<Record<string, InlineCountdownConfig>>(
        step,
        `${basePath}.templateParams.countdowns`
      ) || {};
      const parentTextStyles = getValueByPath<Partial<LabelBlockType>>(
        step,
        `${basePath}.appearance`
      ) || {};

      const updateText = (text: string) => {
        props.onChange(text);
      };

      return (
        <TiptapTemplateEditor
          value={props.value}
          onChange={updateText}
          disabled={props.disabled}
          placeholder={props.placeholder}
          existingLinks={links}
          existingLabels={labels}
          existingCountdowns={countdowns}
          onAddLink={(name, url, updatedText) => {
            if (updatedText !== undefined) updateText(updatedText);
            onChange(`${basePath}.templateParams.links.${name}`, url);
            ensureItemTextDynamicKind(basePath);
          }}
          onAddLabel={(name, labelBlock, updatedText) => {
            if (updatedText !== undefined) updateText(updatedText);
            onChange(`${basePath}.templateParams.labels.${name}`, labelBlock);
            ensureItemTextDynamicKind(basePath);
          }}
          onAddCountdown={(name, config, updatedText) => {
            if (updatedText !== undefined) updateText(updatedText);
            onChange(`${basePath}.templateParams.countdowns.${name}`, config);
            ensureItemTextDynamicKind(basePath);
          }}
          onDeleteLink={(name, updatedText) => {
            updateText(updatedText);
            onChange(`${basePath}.templateParams.links.${name}`, null);
          }}
          onDeleteLabel={(name, updatedText) => {
            updateText(updatedText);
            onChange(`${basePath}.templateParams.labels.${name}`, null);
          }}
          onDeleteCountdown={(name, updatedText) => {
            updateText(updatedText);
            onChange(`${basePath}.templateParams.countdowns.${name}`, null);
          }}
          parentTextStyles={parentTextStyles}
          screenType={step.screenType}
          flowVariables={flowVariableItems}
          data-testid={props.testId}
        />
      );
    },
    [step, onChange, ensureItemTextDynamicKind, flowVariableItems]
  );

  const handleItemImageChange = useCallback(
    (index: number, value: string | null) => {
      if (value) {
        const asset = lastUploadedRef.current ?? { original: value, small: value };
        onChange(`content.stepper.items.${index}.content.image.translations.${locale}`, asset);
        lastUploadedRef.current = null;
      } else {
        onChange(`content.stepper.items.${index}.content.image.translations.${locale}`, null);
      }
    },
    [onChange, locale]
  );

  const handleImageUpload = useCallback(
    async (file: File): Promise<string> => {
      const result = await uploadService.uploadImage(file, { projectId: projectId!, onboardingId: onboardingId!, versionId: versionId!, locale });
      lastUploadedRef.current = { original: result.assetUrl.origin, small: result.assetUrl.small };
      return result.assetUrl.origin;
    },
    [uploadService, projectId, onboardingId, versionId, locale]
  );

  // ============================================
  // Add/Delete/Reorder handlers
  // ============================================

  const handleAddItem = useCallback(() => {
    if (!canAddSlide(rawItems.length)) return;

    const currentDurations = editableSegments.map((s) => s.durationPercent);
    const newDurations = redistributeAfterAdd(currentDurations);
    const newRanges = durationsToSegmentRanges(newDurations);

    const newRange = newRanges[newRanges.length - 1];
    const newSegment = cloneTemplateItem<Record<string, unknown>>(rawItems) ?? createDefaultV2Segment(
      locale,
      newRange.progressStart,
      newRange.progressEnd
    );

    newSegment.progressStart = newRange.progressStart;
    newSegment.progressEnd = newRange.progressEnd;

    const content = (newSegment.content && typeof newSegment.content === 'object'
      ? newSegment.content
      : {}) as Record<string, unknown>;
    content.title = resetLocalizedLabelContent(content.title, locale);
    content.subtitle = resetLocalizedLabelContent(content.subtitle, locale);
    content.description = resetLocalizedLabelContent(content.description, locale);
    content.image = resetAssetTranslations(content.image);
    newSegment.content = content;

    const updatedItems = rawItems.map((item, i) => ({
      ...item,
      progressStart: newRanges[i].progressStart,
      progressEnd: newRanges[i].progressEnd,
    }));
    updatedItems.push(newSegment as (typeof updatedItems)[number]);

    onChange('content.stepper.items', updatedItems);
  }, [editableSegments, rawItems, locale, onChange]);

  const handleDeleteRequest = useCallback(
    (index: number) => {
      const item = editableSegments.find((i) => i.originalIndex === index);
      if (!item) return;

      setDeleteDialogState({
        open: true,
        itemIndex: index,
        itemTitle: item.title || `Segment ${index + 1}`,
        hasConnections: false,
      });
    },
    [editableSegments]
  );

  const handleDeleteConfirm = useCallback(() => {
    const { itemIndex } = deleteDialogState;
    const currentDurations = editableSegments.map((s) => s.durationPercent);
    const newDurations = redistributeAfterRemove(currentDurations, itemIndex);
    const newRanges = durationsToSegmentRanges(newDurations);

    const newItems = rawItems
      .filter((_, i) => i !== itemIndex)
      .map((item, i) => ({
        ...item,
        progressStart: newRanges[i].progressStart,
        progressEnd: newRanges[i].progressEnd,
      }));

    onChange('content.stepper.items', newItems);
    setDeleteDialogState({ open: false, itemIndex: -1, itemTitle: '', hasConnections: false });
  }, [deleteDialogState, editableSegments, rawItems, onChange]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogState({ open: false, itemIndex: -1, itemTitle: '', hasConnections: false });
  }, []);

  const handleReorder = useCallback(
    (reorderedItems: EditableSegment[]) => {
      const newRawItems = reorderedItems.map((item) => rawItems[item.originalIndex]);
      onChange('content.stepper.items', newRawItems);
    },
    [rawItems, onChange]
  );

  // ============================================
  // Computed values
  // ============================================

  const canAdd = canAddSlide(editableSegments.length);
  const canDelete = canRemoveSlide(editableSegments.length);

  const SECTION_NAME = 'Stepper';

  return (
    <SAccordion
      title="Progress Steps"
      collapsible
      defaultExpanded={defaultExpanded}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId(SECTION_NAME)}
    >
      {/* Progress Screen Kind Selector + Styles Gear Icon */}
      <Box sx={{ mb: FIELD_MARGIN_BOTTOM }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Progress Screen Kind
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ flex: 1 }} disabled={disabled}>
            <Select
              value={kind}
              onChange={handleKindChange}
              data-testid="stepper-kind-select"
            >
              {V2_STEPPER_KIND_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Gear icon for styles toggle */}
          <IconButton
            onClick={handleStylesClick}
            disabled={disabled}
            size="small"
            data-testid="stepper-styles-gear"
            sx={stylesExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Style Sections (collapsible via gear icon) */}
      <Collapse in={stylesExpanded} timeout="auto">
        <Box sx={{ mb: FIELD_MARGIN_BOTTOM }} data-testid="stepper-style-sections-container">
          <V2ListStyleSections
            sections={stepperStyleSections}
            currentItemType={kind}
            step={step}
            onChange={onChange}
            disabled={disabled}
            itemsBasePath="content.stepper.items"
          />
        </Box>
      </Collapse>

      {/* Timer Duration + Play Button Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2,
          mb: FIELD_MARGIN_BOTTOM,
        }}
        data-testid="stepper-timer-container"
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Total Duration (seconds)
          </Typography>
          <TextField
            data-testid="stepper-timer-input"
            type="number"
            size="small"
            value={timerDuration}
            onChange={handleTimerDurationChange}
            disabled={disabled}
            fullWidth
            inputProps={{
              min: 1,
              max: 120,
              step: 1,
            }}
          />
        </Box>
        <PlayPauseButton
          onPlay={handlePlay}
          onPause={handleStop}
          isPlaying={isPreviewPlaying}
          disabled={disabled}
        />
      </Box>

      {/* Add Item Button - aligned right */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <AddSlideButton
          onAdd={handleAddItem}
          canAddMore={canAdd}
          disabled={disabled}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Items List with drag-and-drop */}
      <Box data-testid="stepper-items-container">
        {editableSegments.length > 0 ? (
          <SortableList
            items={editableSegments}
            onReorder={handleReorder}
            gap="sm"
            disabled={disabled}
          >
            {(segment: EditableSegment, index: number) => (
              <SortableList.Item
                key={segment.id}
                showDragHandle
                dragHandlePosition="start"
                disabled={disabled}
              >
                <ProgressBarItemEditor
                  index={segment.originalIndex}
                  title={segment.title}
                  subtitle={segment.subtitle}
                  description={segment.description}
                  imageUrl={segment.imageUrl}
                  progressStart={segment.progressStart}
                  progressEnd={segment.progressEnd}
                  range={ranges[index] || [0, 100]}
                  totalDurationSeconds={timerDuration}
                  isFirst={index === 0}
                  isLast={index === editableSegments.length - 1}
                  hasConnections={segment.hasConnections}
                  visibility={visibility}
                  onTitleChange={(value) => handleItemTitleChange(segment.originalIndex, value)}
                  onSubtitleChange={(value) => handleItemSubtitleChange(segment.originalIndex, value)}
                  onDescriptionChange={(value) => handleItemDescriptionChange(segment.originalIndex, value)}
                  onImageChange={(value) => handleItemImageChange(segment.originalIndex, value)}
                  onImageUpload={handleImageUpload}
                  onRangeChange={(newRange) => handleItemRangeChange(index, newRange)}
                  onDelete={() => handleDeleteRequest(segment.originalIndex)}
                  canDelete={canDelete}
                  disabled={disabled}
                  renderTitleField={(props) => renderTemplateTextField(segment.originalIndex, 'title', props)}
                  renderSubtitleField={(props) => renderTemplateTextField(segment.originalIndex, 'subtitle', props)}
                  renderDescriptionField={(props) => renderTemplateTextField(segment.originalIndex, 'description', props)}
                />
              </SortableList.Item>
            )}
          </SortableList>
        ) : (
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            No segments. Click &quot;Add Slide&quot; to create one.
          </Typography>
        )}
      </Box>

      {/* Delete confirmation dialog */}
      <DeleteItemDialog
        open={deleteDialogState.open}
        itemIndex={deleteDialogState.itemIndex}
        itemTitle={deleteDialogState.itemTitle}
        hasConnections={deleteDialogState.hasConnections}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </SAccordion>
  );
};
